import type { RegionKey } from "./sql";
import { MONTH_COUNT, CURRENT_MONTH_IDX } from "./helpers";
import csvText from "./static-invoices.csv?raw";

type RegionMap = Record<RegionKey, number>;

type InvoiceRow = {
  mrr: number;
  country: RegionKey;
  start: string | null;
  end: string | null;
  product: "Pro" | "Plus";
  active: boolean;
};

const COUNTRY_MAP: Record<string, RegionKey> = {
  NZ: "NZ",
  AU: "AU",
  UK: "UK",
  Other: "Other",
};

function parseMrr(raw: string): number {
  const cleaned = raw.replace(/[$£,\s]/g, "");
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
}

function parseRows(): InvoiceRow[] {
  const lines = csvText.trim().split("\n");
  // Skip header
  return lines.slice(1).map((line) => {
    // Handle quoted fields (e.g. "$2,409" or "21 seats × A$79.20/mo, billed quarterly")
    const cols: string[] = [];
    let cur = "";
    let inQuote = false;
    for (const ch of line) {
      if (ch === '"') { inQuote = !inQuote; continue; }
      if (ch === "," && !inQuote) { cols.push(cur.trim()); cur = ""; continue; }
      cur += ch;
    }
    cols.push(cur.trim());

    const country = COUNTRY_MAP[cols[2] ?? ""] ?? "Other";
    const product = (cols[5] === "Pro" ? "Pro" : "Plus") as "Pro" | "Plus";
    const active = (cols[7] ?? "").toLowerCase() === "yes";
    const startRaw = cols[3] ?? "";
    const endRaw = cols[4] ?? "";

    return {
      mrr: parseMrr(cols[1] ?? "0"),
      country,
      start: startRaw.includes("/") ? startRaw : null,
      end: endRaw.includes("/") ? endRaw : null,
      product,
      active,
    };
  });
}

const ROWS = parseRows();

function parseDate(s: string): Date {
  const parts = s.split("/").map(Number);
  return new Date(parts[2] as number, (parts[1] as number) - 1, parts[0]);
}

function monthIdx(d: Date): number {
  return (d.getFullYear() - 2025) * 12 + d.getMonth() - 8;
}

export function invoicedHistory(): Record<RegionKey, number[]> {
  const out: Record<RegionKey, number[]> = {
    NZ: Array(MONTH_COUNT).fill(0) as number[],
    AU: Array(MONTH_COUNT).fill(0) as number[],
    UK: Array(MONTH_COUNT).fill(0) as number[],
    Other: Array(MONTH_COUNT).fill(0) as number[],
  };

  for (const row of ROWS) {
    if (!row.active || row.mrr <= 0) continue;
    const startIdx = row.start ? monthIdx(parseDate(row.start)) : 0;
    const endIdx = row.end ? monthIdx(parseDate(row.end)) : MONTH_COUNT - 1;
    const lastIdx = Math.min(MONTH_COUNT - 1, endIdx);
    for (let i = Math.max(0, startIdx); i <= lastIdx; i++) {
      out[row.country]![i]! += row.mrr;
    }
  }

  for (const r of Object.keys(out) as RegionKey[]) {
    for (let i = 0; i < MONTH_COUNT; i++) {
      (out[r] as number[])[i] = Math.round((out[r] as number[])[i] as number);
    }
  }

  return out;
}

export function invoicedByTier(): {
  mrr: { Pro: RegionMap; Plus: RegionMap };
  firms: { Pro: RegionMap; Plus: RegionMap };
} {
  const mrr = {
    Pro: { NZ: 0, AU: 0, UK: 0, Other: 0 } as RegionMap,
    Plus: { NZ: 0, AU: 0, UK: 0, Other: 0 } as RegionMap,
  };
  const firms = {
    Pro: { NZ: 0, AU: 0, UK: 0, Other: 0 } as RegionMap,
    Plus: { NZ: 0, AU: 0, UK: 0, Other: 0 } as RegionMap,
  };

  for (const row of ROWS) {
    if (!row.active || row.mrr <= 0) continue;
    const startIdx = row.start ? monthIdx(parseDate(row.start)) : 0;
    const endIdx = row.end ? monthIdx(parseDate(row.end)) : MONTH_COUNT - 1;
    if (CURRENT_MONTH_IDX < startIdx || CURRENT_MONTH_IDX > endIdx) continue;
    mrr[row.product][row.country] += row.mrr;
    firms[row.product][row.country] += 1;
  }

  for (const tier of ["Pro", "Plus"] as const) {
    for (const r of Object.keys(mrr[tier]) as RegionKey[]) {
      mrr[tier][r] = Math.round(mrr[tier][r]);
    }
  }

  return { mrr, firms };
}
